import { useCallback, useMemo } from 'react';

import { useAudioQueue } from 'Services/Audio/useAudioQueue';
import AddToQueueSvg from 'Shared/svg/addToQueue.svg';
import { trackAddToQueueFromPage } from 'UI/Components/AudioPlayer/biTrack';
import { useAudioHandlers } from 'Services/Audio/useAudioHandlers';
import { isWrapper } from 'Wrapper/criticals';
import { useAuthorizedUser } from 'Services/User/useAuthorizedUser';

import { useIsAudioAvailable } from '../useIsAudioAvailable';
import { getAudioUrl } from '../getAudioUrl';

import type { FullArticleT } from 'Shared/types/ApiModels';
import type { ArticleTypeT } from 'Constants/articleTypes';
import type { ServicePlanArticleT } from 'Services/api/types';

type PropsT = {
  readonly article: FullArticleT | ServicePlanArticleT;
  readonly articleType: ArticleTypeT;
  readonly buttonRef?: React.Ref<HTMLButtonElement>;
  readonly hideLabel?: boolean;
  readonly isMpPaidSubscriber?: boolean;
  readonly mpServicePlanId?: string;
  readonly id?: string;
};

export const AddToQueueButton = ({
  article,
  articleType,
  buttonRef,
  hideLabel,
  mpServicePlanId,
  id,
}: PropsT) => {
  const { openPlayer } = useAudioHandlers();
  const { addToQueue, getIsInQueue, isAddingToQueue, isFetching } =
    useAudioQueue();
  const isAudioAvailable = useIsAudioAvailable(
    article.audioDuration,
    articleType,
  );
  const url = getAudioUrl(article.id, articleType, mpServicePlanId);
  const { isAnyProSubscriber } = useAuthorizedUser();

  const isInQueue = useMemo(() => {
    return getIsInQueue({
      subjectId: article.id,
      subjectType: articleType,
    });
  }, [article.id, articleType, getIsInQueue]);

  const handleClick = useCallback(() => {
    if (!isInQueue && !isAddingToQueue && !isFetching) {
      trackAddToQueueFromPage(article.id);

      addToQueue({
        subjectType: articleType,
        subjectId: article.id,
        url,
      });
    }

    openPlayer({
      showQueue: isAnyProSubscriber && (!isWrapper || isInQueue),
    });
  }, [
    isInQueue,
    isAddingToQueue,
    isFetching,
    openPlayer,
    isAnyProSubscriber,
    article.id,
    addToQueue,
    articleType,
    url,
  ]);

  if (!isAudioAvailable) {
    return null;
  }

  return (
    <button
      aria-label={
        isInQueue ? 'Added to queue, click to open queue' : 'Add to queue'
      }
      className="text-share-link"
      id={id}
      ref={buttonRef}
      type="button"
      onClick={handleClick}
    >
      <span className="flex items-center">
        <AddToQueueSvg />

        {hideLabel ? null : (
          <span className="ml-8 text-medium-b">{`${isInQueue ? 'Added' : 'Add'} to queue`}</span>
        )}
      </span>
    </button>
  );
};
